import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { mockArtworks } from '../data/mockArtworks';
import ArtworkModal from '../components/ArtworkModal';
import { Filter } from 'lucide-react';

const Gallery = () => {
  const { t } = useLanguage();
  const [selectedSeries, setSelectedSeries] = useState('all');
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [artworks, setArtworks] = useState(mockArtworks);

  useEffect(() => {
    // Load custom artworks from localStorage if they exist
    const customArtworks = localStorage.getItem('customArtworks');
    if (customArtworks) {
      setArtworks(JSON.parse(customArtworks));
    }
  }, []);

  const filteredArtworks = selectedSeries === 'all'
    ? artworks
    : artworks.filter(art => art.series === selectedSeries);

  const seriesOptions = [
    { value: 'all', label: t.gallery.series.all },
    { value: 'saintMartin', label: t.gallery.series.saintMartin },
    { value: 'rise', label: t.gallery.series.rise },
    { value: 'bloc', label: t.gallery.series.bloc },
    { value: 'earlyMozer', label: t.gallery.series.earlyMozer },
    { value: 'origins', label: t.gallery.series.origins }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="container mx-auto px-4">
        <h1 className="text-5xl md:text-6xl font-bold mb-12 text-center">{t.gallery.title}</h1>

        {/* Filters */}
        <div className="mb-12 flex items-center justify-center">
          <div className="inline-flex items-center space-x-3 bg-white rounded-lg p-2 shadow-sm">
            <Filter className="w-5 h-5 text-gray-500 ml-2" />
            <select
              value={selectedSeries}
              onChange={(e) => setSelectedSeries(e.target.value)}
              className="px-4 py-2 bg-transparent border-none outline-none cursor-pointer font-medium text-gray-700"
            >
              {seriesOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Artwork Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredArtworks.map((artwork) => (
            <div
              key={artwork.id}
              onClick={() => setSelectedArtwork(artwork)}
              className="group cursor-pointer bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all transform hover:scale-105"
            >
              <div className="aspect-square bg-gray-200 overflow-hidden relative">
                <img
                  src={artwork.image}
                  alt={artwork.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1536924940846-227afb31e2a5?w=500&h=500&fit=crop';
                  }}
                />
                {/* Availability Badge */}
                <div className="absolute top-3 right-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    artwork.available
                      ? 'bg-green-500 text-white'
                      : 'bg-red-500 text-white'
                  }`}>
                    {artwork.available ? t.gallery.available : t.gallery.sold}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-lg mb-1">{artwork.title}</h3>
                <p className="text-sm text-gray-600 mb-2">{artwork.year}</p>
                <p className="text-sm text-gray-500">{artwork.size}</p>
                <p className="font-semibold text-gray-900 mt-2">{artwork.price} €</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Artwork Modal */}
      {selectedArtwork && (
        <ArtworkModal
          artwork={selectedArtwork}
          onClose={() => setSelectedArtwork(null)}
        />
      )}
    </div>
  );
};

export default Gallery;
